import { useEffect, useRef, useState } from "react";
import axios from "axios";
import type { CustomAxiosRequestConfig } from "../../util/axios";
import Hls from "hls.js";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Label,
  Spinner,
  TextInput,
} from "flowbite-react";
import { HiHome, HiPlus, HiTrash } from "react-icons/hi";
import { HiMiniPause, HiMiniPlay } from "react-icons/hi2";

import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import {
  generateUploadUrl,
  completeUpload,
  updatePlaylist,
  deletePlaylistItem,
  getPlaylist,
} from "../../services/radio";
import SortableItem from "../programs/SortableItem";
import FileUploadZone from "../media/FileUploadZone";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { ToastContainer } from "react-toastify";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { MdDragIndicator } from "react-icons/md";

const ManagePlaylistPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [newTitle, setNewTitle] = useState("");
  const [newArtist, setNewArtist] = useState("");
  const [musicFile, setMusicFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  const [currentLoadingId, setCurrentLoadingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadPlaylist = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res = await getPlaylist(id);
        const playlistData = res.data.results;
        setPlaylist(playlistData);
        setItems(
          (playlistData.items || []).map((it: any, i: number) => ({
            ...it,
            order: i,
          })),
        );
      } catch (e) {
        toast.error("Failed to load playlist");
        navigate("/radio");
      } finally {
        setLoading(false);
      }
    };

    loadPlaylist();
  }, [id, navigate]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setCurrentPlayingId(null);
      setCurrentLoadingId(null);
    };
  }, []);

  const handlePlayPause = (item: any) => {
    if (currentPlayingId === item.id && audioRef.current) {
      audioRef.current.pause();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setCurrentPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      setCurrentLoadingId(item.id);
      setCurrentPlayingId(null);

      const audio = new Audio();
      audioRef.current = audio;

      const onError = (error: any) => {
        console.error("Error playing audio:", error);
        toast.error("Failed to play audio");
        setCurrentPlayingId(null);
        setCurrentLoadingId(null);
      };

      audio.addEventListener("ended", () => {
        setCurrentPlayingId(null);
      });

      audio.addEventListener("error", onError);

      const playAudio = () => {
        audio
          .play()
          .then(() => {
            setCurrentPlayingId(item.id);
            setCurrentLoadingId(null);
          })
          .catch(onError);
      };

      if (Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls;
        hls.attachMedia(audio);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(item.url);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, playAudio);
        hls.on(Hls.Events.ERROR, (_, data) => {
          console.error("HLS error:", data);
          setCurrentLoadingId(null);
        });
      } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
        audio.src = item.url;
        playAudio();
      } else {
        audio.src = item.url;
        playAudio();
      }
    }
  };

  const IGNORE_TAGS = ["BUTTON"];

  const customHandleEvent = (element: HTMLElement | null) => {
    let cur = element;
    while (cur) {
      if (IGNORE_TAGS.includes(cur.tagName) || cur.dataset["noDnd"]) {
        return false;
      }
      cur = cur.parentElement;
    }
    return true;
  };

  MouseSensor.activators = [
    {
      eventName: "onMouseDown",
      handler: ({ nativeEvent }: { nativeEvent: MouseEvent }) =>
        customHandleEvent(nativeEvent.target as HTMLElement),
    },
  ];

  TouchSensor.activators = [
    {
      eventName: "onTouchStart",
      handler: ({ nativeEvent }: { nativeEvent: TouchEvent }) =>
        customHandleEvent(nativeEvent.target as HTMLElement),
    },
  ];

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setItems((cur) => {
        const oldIndex = cur.findIndex((it) => it.id === active.id);
        const newIndex = cur.findIndex((it) => it.id === over.id);
        const newItems = arrayMove(cur, oldIndex, newIndex).map((it, idx) => ({
          ...it,
          order: idx,
        }));
        return newItems;
      });
    }
  };

  const handleAddItem = async () => {
    if (!newTitle.trim() || !newArtist.trim() || !musicFile || !playlist)
      return;
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      const urlRes = await generateUploadUrl({
        title: newTitle,
        artist: newArtist,
        radio_playlist: playlist.id,
        order: items.length,
        filename: musicFile.name,
        content_type: musicFile.type,
      });

      const { upload_url, playlist_item } = urlRes.data.results;

      await axios.put(upload_url, musicFile, {
        headers: { "Content-Type": musicFile.type },
        skipAuth: true,
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress((e.loaded / e.total) * 100);
          }
        },
      } as CustomAxiosRequestConfig);

      await completeUpload(playlist_item.id);

      setItems([...items, playlist_item]);
      setNewTitle("");
      setNewArtist("");
      setMusicFile(null);
      setUploadProgress(0);
      toast.success("Item added");
    } catch (e) {
      toast.error("Failed to add item");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleSaveOrder = async () => {
    if (!playlist) return;
    setIsSubmitting(true);
    try {
      await updatePlaylist(playlist.id, {
        items_order: items.map((it) => ({ id: it.id, order: it.order })),
      });
      toast.success("Playlist updated");
      navigate("/radio");
    } catch (e) {
      toast.error("Failed to update playlist");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    setIsSubmitting(true);
    try {
      await deletePlaylistItem(id);
      setItems(
        items
          .filter((i) => i.id !== id)
          .map((it, idx) => ({
            ...it,
            order: idx,
          })),
      );
      toast.success("Item deleted");
    } catch (e) {
      toast.error("Failed to delete item");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusBadge = (status: string) => {
    if (!status) {
      return null;
    }

    switch (status.toUpperCase()) {
      case "PROCESSING":
        return (
          <Badge color="yellow" className="text-xs">
            Processing
          </Badge>
        );
      case "FAILURE":
        return (
          <Badge color="failure" className="text-xs">
            Failed
          </Badge>
        );
      default:
        return null;
    }
  };

  const canPlayItem = (item: any) => {
    const status = item.processing_status?.toUpperCase();
    return status !== "PROCESSING" && status !== "FAILURE";
  };

  if (loading) {
    return (
      <NavbarSidebarLayout isFooter={false}>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (!playlist) {
    return (
      <NavbarSidebarLayout isFooter={false}>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Playlist not found</div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/radio">Radio</BreadcrumbItem>
              <BreadcrumbItem>Manage {playlist.title}</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Manage {playlist.title} Items
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div ref={rootRef} className="max-w-4xl">
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Playlist Items ({items.length})
            </h2>
            {items.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                No items in this playlist yet. Add some items below.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={items.map((it) => it.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {items.map((item) => (
                      <SortableItem key={item.id} id={item.id}>
                        {({
                          setNodeRef,
                          attributes,
                          listeners,
                          style,
                        }: any) => (
                          <div
                            ref={setNodeRef}
                            style={style}
                            className="flex items-center gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-800 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                          >
                            <MdDragIndicator
                              className="cursor-grab text-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              {...attributes}
                              {...listeners}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {item.title} - {item.artist}
                                </span>
                                {renderStatusBadge(item.processing_status)}
                              </div>
                            </div>
                            <Button
                              color="primary"
                              size="sm"
                              onPointerDown={(e) => e.stopPropagation()}
                              data-no-dnd="true"
                              onClick={() => handlePlayPause(item)}
                              disabled={isSubmitting || !canPlayItem(item)}
                            >
                              {currentLoadingId === item.id ? (
                                <div className="flex items-center justify-center w-4 h-4">
                                  <Spinner size="sm" />
                                </div>
                              ) : currentPlayingId === item.id ? (
                                <div className="flex items-center justify-center w-4 h-4">
                                  <HiMiniPause className="text-sm" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center w-4 h-4">
                                  <HiMiniPlay className="text-sm" />
                                </div>
                              )}
                            </Button>
                            <Button
                              color="failure"
                              size="sm"
                              onPointerDown={(e) => e.stopPropagation()}
                              data-no-dnd="true"
                              onClick={() => handleDeleteItem(item.id)}
                              disabled={isSubmitting}
                            >
                              <HiTrash className="text-sm" />
                            </Button>
                          </div>
                        )}
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <div className="mt-6 flex gap-3">
              <Button
                color="primary"
                onClick={handleSaveOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                color="light"
                onClick={() => navigate("/radio")}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Add New Item
            </h2>
            <div className="mb-4 flex flex-col gap-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <TextInput
                  id="title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Song title"
                />
              </div>
              <div>
                <Label htmlFor="artist">Artist</Label>
                <TextInput
                  id="artist"
                  value={newArtist}
                  onChange={(e) => setNewArtist(e.target.value)}
                  placeholder="Artist"
                />
              </div>
              <div>
                <Label className="mb-2 block">Audio File</Label>
                <FileUploadZone
                  maxSize={50 * 1024 * 1024}
                  acceptedTypes={[
                    "audio/mpeg",
                    "audio/aac",
                    "audio/mp4",
                    "audio/ogg",
                  ]}
                  onFileChange={(file) => setMusicFile(file)}
                />
              </div>
              <Button
                color="primary"
                onClick={handleAddItem}
                disabled={
                  isSubmitting ||
                  !newTitle.trim() ||
                  !newArtist.trim() ||
                  !musicFile
                }
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-x-2">
                    <Spinner size="sm" />
                    <span>Uploading {Math.round(uploadProgress)}%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-x-2">
                    <HiPlus className="text-xl" />
                    Add Item
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        theme="dark"
      />
    </NavbarSidebarLayout>
  );
};

export default ManagePlaylistPage;
